import type { AdminDemoDto, DemoUpdateDto } from '@/castapi';
import { AppLogger } from '@/logger';
import { pick } from '@/utils/helpers/data';
import { getDemosApi, getErrorMessage } from '@/castapi/helpers';
import type { IActionParams } from '@/store/modules/index';

const logger = new AppLogger('adminDemos state');

interface IAdminDemosState {
  demos: AdminDemoDto[];
  demosLoading: boolean;
  demosLoadError?: string;
  demo?: AdminDemoDto;
  demoLoading: boolean;
  demoLoadError?: string;
  demoSaving: boolean;
  demoSaveError?: string;
}

const initialState = (): IAdminDemosState => ({
  demos: [],
  demosLoading: false,
  demosLoadError: undefined,
  demo: undefined,
  demoLoading: false,
  demoLoadError: undefined,
  demoSaving: false,
  demoSaveError: undefined,
});

type ActionParams = IActionParams<IAdminDemosState>;

export default {
  namespaced: true,
  state: initialState,
  mutations: {
    DEMOS_LOADING(state: IAdminDemosState): void {
      state.demosLoadError = undefined;
      state.demosLoading = true;
    },
    DEMOS_LOADED(state: IAdminDemosState, payload: AdminDemoDto[]): void {
      state.demos = payload;
      state.demosLoading = false;
    },
    DEMOS_LOAD_ERROR(state: IAdminDemosState, error: Error): void {
      state.demosLoadError = getErrorMessage(error);
      state.demosLoading = false;
    },
    DEMO_LOADING(state: IAdminDemosState): void {
      state.demoLoadError = undefined;
      state.demoLoading = true;
    },
    DEMO_LOADED(state: IAdminDemosState, payload: AdminDemoDto): void {
      state.demo = payload;
      state.demoLoading = false;
    },
    DEMO_LOAD_ERROR(state: IAdminDemosState, error: Error): void {
      state.demosLoadError = getErrorMessage(error);
      state.demoLoading = false;
    },
    DEMO_UPDATE_IN_PROGRESS(state: IAdminDemosState): void {
      state.demoSaving = true;
      state.demoSaveError = undefined;
    },
    DEMO_UPDATE_SUCCESS(state: IAdminDemosState, payload: AdminDemoDto): void {
      const ind = state.demos.findIndex(item => item.demoId === payload.demoId);
      Object.assign(state.demos[ind], payload);
      state.demoSaving = false;
    },
    DEMO_UPDATE_ERROR(state: IAdminDemosState, error: Error): void {
      state.demoSaving = false;
      state.demoSaveError = getErrorMessage(error);
    },
    RESET_STATE(state: IAdminDemosState): void {
      Object.assign(state, initialState());
    },
    RESET_EDITOR_STATE(state: IAdminDemosState): void {
      state.demo = undefined;
      state.demoLoading = false;
      state.demoLoadError = undefined;
      state.demoSaving = false;
      state.demoSaveError = undefined;
    },
  },
  actions: {
    async loadDemos({ commit, rootGetters }: ActionParams): Promise<void> {
      const accessToken = rootGetters['login/accessToken'];
      try {
        commit('DEMOS_LOADING');
        const response = await getDemosApi(accessToken).demoControllerAdminFindAll();
        commit('DEMOS_LOADED', response.data);
      } catch (error) {
        commit('DEMOS_LOAD_ERROR', error);
        logger.captureStoreError('loadDemos', error);
      }
    },
    async loadDemo({ commit, rootGetters }: ActionParams, id: number): Promise<void> {
      const accessToken = rootGetters['login/accessToken'];
      try {
        commit('DEMO_LOADING');
        const response = await getDemosApi(accessToken).demoControllerAdminGet(id);
        commit('DEMO_LOADED', response.data);
      } catch (error) {
        commit('DEMO_LOAD_ERROR', error);
        logger.captureStoreError('loadDemos', error, { id });
      }
    },
    async updateDemo({ commit, rootGetters }: ActionParams, demo: AdminDemoDto): Promise<void> {
      try {
        commit('DEMO_UPDATE_IN_PROGRESS');
        const demoUpdateDto = pick(demo, ['downloadPath', 'description', 'resources', 'guides']) as DemoUpdateDto;
        await getDemosApi(rootGetters['login/accessToken']).demoControllerUpdate(demo.demoId, demoUpdateDto);
        commit('DEMO_UPDATE_SUCCESS', demo);
      } catch (error) {
        commit('DEMO_UPDATE_ERROR', error);
        logger.captureStoreError('updateDemo', error, { demo });
      }
    },
    resetState({ commit }: ActionParams): void {
      commit('RESET_STATE');
    },
    resetEditorState({ commit }: ActionParams): void {
      commit('RESET_EDITOR_STATE');
    },
  },
  getters: {
    demos: (state: IAdminDemosState): AdminDemoDto[] => state.demos,
    demosLoading: (state: IAdminDemosState): boolean => state.demosLoading,
    demosLoadError: (state: IAdminDemosState): undefined | string => state.demosLoadError,
    demo: (state: IAdminDemosState): undefined | AdminDemoDto => state.demo,
    demoLoading: (state: IAdminDemosState): boolean => state.demoLoading,
    demoLoadError: (state: IAdminDemosState): undefined | string => state.demoLoadError,
    demoSaving: (state: IAdminDemosState): boolean => state.demoSaving,
    demoSaveError: (state: IAdminDemosState): undefined | string => state.demoSaveError,
  },
};
